import React, { useEffect, useState } from 'react';
import api from '../../../api/api.js';
import { supabase } from '../../../services/supabase/supabaseClient.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { YoolTable, StatusPill, MonoText, StackedCell } from '../../../components/ui/YoolTable';
import { RefreshCw, Upload, PlayCircle, CheckCircle, Minus } from 'lucide-react';
import SectionHeader from '../../../components/ui/fancy/SectionHeader';
import { KpiGrid } from '../../../components/ui/KpiCard';
import ErrorMessage from '../../../components/ErrorMessage';
import { useNavigate } from 'react-router-dom';

const STATUS_ALIASES = {
  ok:             'pagado',
  diferencia:     'pendiente',
  no_facturado:   'error',
  no_sistema:     'error',
  sin_pago:       'inactivo',
  match_probable: 'procesando',
  pendiente:      'pendiente',
};

const formatClp = (v) =>
  v != null ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v) : '—';
const formatUf = (v) => v != null ? `${parseFloat(v).toFixed(4)} UF` : '—';

export default function ConciliacionList() {
  const navigate = useNavigate();
  const [companias, setCompanias] = useState([]);
  const [companiaId, setCompaniaId] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [periodo, setPeriodo] = useState('');
  const [resultados, setResultados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionMsg, setActionMsg] = useState('');

  useEffect(() => {
    supabase.from('companias').select('id, nombre_corto').order('nombre_corto').then(({ data }) => {
      setCompanias(data || []);
    });
  }, []);

  useEffect(() => {
    if (!companiaId) return;
    const ps = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      ps.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    }
    setPeriodos(ps);
    if (!periodo) setPeriodo(ps[0]);
  }, [companiaId]);

  const cargarResultados = async () => {
    if (!companiaId || !periodo) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/conciliacion/resultados', {
        params: { companiaId, periodo }
      });
      if (!data.success) throw new Error(data?.error || 'Error al cargar');
      setResultados(data.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargarResultados(); }, [companiaId, periodo]);

  const handleGenerarDevengados = async () => {
    setActionMsg('Generando devengados...');
    try {
      const { data } = await api.post('/api/conciliacion/generar-devengados', { companiaId, periodo });
      setActionMsg(`Devengados: ${data.devengados} | Pagados: ${data.pagados} | Insertados: ${data.insertados}`);
      cargarResultados();
    } catch (e) {
      setActionMsg('Error: ' + e.message);
    }
  };

  const handleEjecutarMatching = async () => {
    setActionMsg('Ejecutando matching...');
    try {
      const { data } = await api.post('/api/conciliacion/ejecutar-matching', { companiaId, periodo });
      setActionMsg(`Matching completado: ${data.resultados} resultados`);
      cargarResultados();
    } catch (e) {
      setActionMsg('Error: ' + e.message);
    }
  };

  const kpis = [
    { title: 'Total',       value: resultados.length },
    { title: 'OK',          value: resultados.filter(r => r.status === 'ok').length },
    { title: 'Diferencia',  value: resultados.filter(r => r.status === 'diferencia').length },
    { title: 'Sin factura', value: resultados.filter(r => r.status === 'no_facturado').length },
    { title: 'Sin sistema', value: resultados.filter(r => r.status === 'no_sistema').length },
    { title: 'Probable',    value: resultados.filter(r => r.status === 'match_probable').length },
  ];

  return (
    <div className="p-6 space-y-6">
      <SectionHeader
        title="Conciliación de Primas"
        subtitle="Cruce de primas devengadas, pagadas y facturadas"
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-64">
              <label className="text-sm font-medium mb-1 block">Compañía</label>
              <Select value={companiaId} onValueChange={setCompaniaId}>
                <SelectTrigger><SelectValue placeholder="Seleccionar compañía" /></SelectTrigger>
                <SelectContent>
                  {companias.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre_corto}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <label className="text-sm font-medium mb-1 block">Período</label>
              <Select value={periodo} onValueChange={setPeriodo}>
                <SelectTrigger><SelectValue placeholder="YYYY-MM" /></SelectTrigger>
                <SelectContent>
                  {periodos.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={cargarResultados} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <Button variant="outline" onClick={handleGenerarDevengados} disabled={!companiaId || !periodo}>
              <PlayCircle className="h-4 w-4 mr-2" />
              Generar devengados
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/payments/conciliacion/${companiaId}/${periodo}/factura`)}
              disabled={!companiaId || !periodo}
            >
              <Upload className="h-4 w-4 mr-2" />
              Cargar factura
            </Button>
            <Button onClick={handleEjecutarMatching} disabled={!companiaId || !periodo}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Ejecutar matching
            </Button>
          </div>
          {actionMsg && <p className="mt-3 text-sm text-muted-foreground">{actionMsg}</p>}
        </CardContent>
      </Card>

      {error && <ErrorMessage message={error} />}

      {resultados.length > 0 && <KpiGrid items={kpis} />}

      <YoolTable
        columns={[
          { key: 'rut',    label: 'RUT',    render: r => <MonoText>{r.rut || '—'}</MonoText> },
          { key: 'nombre', label: 'Nombre', sortable: true },
          { key: 'devengado', label: 'Devengado', align: 'right', numeric: true, sortable: true,
            sortValue: r => r.monto_devengado_clp,
            render: r => <StackedCell primary={formatUf(r.monto_devengado_uf)} secondary={formatClp(r.monto_devengado_clp)} /> },
          { key: 'monto_facturado_clp', label: 'Facturado CLP', align: 'right', numeric: true, sortable: true,
            render: r => <MonoText>{formatClp(r.monto_facturado_clp)}</MonoText> },
          { key: 'monto_pagado_clp',   label: 'Pagado CLP',   align: 'right', numeric: true, sortable: true,
            render: r => <MonoText>{formatClp(r.monto_pagado_clp)}</MonoText> },
          { key: 'diferencia_clp', label: 'Diferencia', align: 'right', numeric: true, sortable: true,
            render: r => r.diferencia_clp != null
              ? <span style={{ fontVariantNumeric: 'tabular-nums', color: r.diferencia_clp === 0 ? 'var(--yool-success)' : 'var(--yool-error)' }}>
                  {formatClp(r.diferencia_clp)}
                </span>
              : <Minus style={{ width: 16, height: 16, color: 'var(--yool-muted)', display: 'inline' }} /> },
          { key: 'status', label: 'Estado',
            render: r => <StatusPill estado={STATUS_ALIASES[r.status] || r.status}>{r.status}</StatusPill> },
          { key: 'match_score', label: 'Score', render: r => <MonoText>{r.match_score ?? '—'}</MonoText> },
        ]}
        rows={resultados}
        rowKey="id"
        search={{ placeholder: 'Buscar por RUT o nombre…', keys: ['rut', 'nombre'] }}
        empty={{ title: 'Sin resultados', text: 'Selecciona compañía y período, genera devengados, carga la factura y ejecuta el matching.' }}
      />
    </div>
  );
}
